import StatusChip from 'components/@extended/StatusChip';
import { FIELD_LABEL_CLASS } from 'components/GridItemList';
import CheckboxInput from 'components/forms/inputs/rhf-inputs/CheckboxInput';
import DecimalInput from 'components/forms/inputs/rhf-inputs/DecimalInput';
import PercentageInput from 'components/forms/inputs/rhf-inputs/PercentageInput';
import TextInput from 'components/forms/inputs/rhf-inputs/TextInput';
import { CircleAlert, CheckIcon, EllipsisIcon, LockIcon, RefreshCwIcon } from 'components/icons';
import InfoTooltip from 'components/InfoTooltip';
import MatterIndicators from 'components/matters/MatterIndicators';
import PartyName from 'components/party/PartyName';
import { EmployeeNameLink } from 'components/record-chip/RecordLink';
import { SectionEdit, SectionEditForm, useEditingSectionId } from 'components/section-edit/SectionEdit';
import SectionUnsavedChangesGuard from 'components/section-edit/SectionUnsavedChangesGuard';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from 'components/ui/alert-dialog';
import { Badge } from 'components/ui/badge';
import { Button, buttonVariants } from 'components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from 'components/ui/dropdown-menu';
import { formatToHours } from 'components/util/HoursDisplay';
import MoneyDisplay from 'components/util/MoneyDisplay';
import isEqual from 'lodash/isEqual';
import { useInvoice } from 'pages/invoices/hooks/useInvoice';
import { useInvoiceMatterForm } from 'pages/invoices/hooks/useInvoiceMatterForm';
import { caseSectionId } from 'pages/invoices/layout/record/invoiceSections';
import { mapInvoiceMatterFormDataToAdjustmentDto } from 'pages/invoices/util/InvoicesPageUtil';
import { ReactNode, useEffect, useId, useState } from 'react';
import { Control, useForm, UseFormReturn } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import {
    useDeleteInvoiceMatterMutation,
    useUpdateInvoiceMatterAdjustmentsMutation,
    useUpdateInvoiceMatterCloseOnSendMutation,
    useUpdateInvoiceMatterMutation,
} from 'store/api/invoice-details';
import { UpdateCloseOnSendDto } from 'types/caseClosing';
import { ClassifierData } from 'types/classifiers';
import { FeatureFlag } from 'types/features';
import { InvoiceMatterForm, InvoiceMatterNameDetailsForm, InvoiceMatterSummary } from 'types/invoice-details';
import { InvoiceData, InvoiceMatterDetailsDto, InvoiceStatusType, InvoiceTransactionActionType } from 'types/invoices';
import { PricingLimitationType } from 'types/matters';
import { cn } from 'utils/cn';
import { getClassifierText, t } from 'utils/getTranslations';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { paymentCurrencyInputClassName, usePaymentCurrencyMode } from 'utils/usePaymentCurrencyMode';
import { useTimecardPrecision } from 'utils/useTimecardPrecision';
import RecalculatingOverlay from '../../../components/RecalculatingOverlay';
import CapFeeBudgetBox from '../../components/CapFeeBudgetBox';
import PaymentCurrencyNotice from '../../components/PaymentCurrencyNotice';
import { AlertActionButton, DetailsAlert } from '../Details';
import { CASE_CARD_CLASS, CASE_FIGURES_CLASS, Figure } from '../SectionCard';
import { CloseOnSendTick } from './closeOnSend/CloseOnSendTick';
import { getUsedHours, useCapLimitAdjustments } from './component/SummaryHeaderLeftSide';
import { useCaseDiscount, validateDiscountRate } from './component/SummaryHeaderRightSide';
import { caseNameValues, useIsMatterRemovable } from './InvoiceMatterNameDetails';
import { CaseSignOffs } from './CaseSignOffs';
import { useCaseApproval } from './useCaseApproval';

const BADGE_CLASS = 'bg-neutral-tint text-foreground-secondary';

/** Whether the case's names have a local-language pair: the gate the names' edit had in its modal. */
const useIsLocalizedFieldsEnabled = () =>
    useIsFeatureEnabled([FeatureFlag.LOCALIZED_FIELDS, FeatureFlag.LOCALIZED_FIELDS_INVOICES]);

/** The case's fee arrangement, its fixed fee, and its confidential mark. */
export function CaseMarks({
    invoice,
    invoiceMatter,
    classifiers,
}: {
    invoice: InvoiceData;
    invoiceMatter: InvoiceMatterDetailsDto;
    classifiers: Record<string, ClassifierData>;
}) {
    const isSplit = invoice.isSplit ?? false;
    const isFixedFee =
        invoiceMatter.feeArrangementTypeCode === PricingLimitationType.FIXED_ONETIME ||
        invoiceMatter.feeArrangementTypeCode === PricingLimitationType.FIXED_RECURRING;
    return (
        <>
            <Badge className={BADGE_CLASS}>
                {isSplit
                    ? t('invoices.split-invoice')
                    : getClassifierText(classifiers['PRICING_LIMITATION_TYPE'], invoiceMatter.feeArrangementTypeCode)}
            </Badge>
            {isFixedFee && !isSplit && invoiceMatter.fixedFee ? (
                <span className="text-meta text-foreground-secondary tabular-nums">
                    <MoneyDisplay amount={invoiceMatter.fixedFee} currency={invoiceMatter.pricelistCurrencyTypeCode} />
                </span>
            ) : null}
            <MatterIndicators isConfidential={invoiceMatter.isConfidential} />
        </>
    );
}

/** The case's number, fee arrangement and marks over its name on this invoice, in read and in edit. */
function CaseIdentity({
    invoice,
    invoiceMatter,
    classifiers,
    headingId,
    showsCapError = false,
}: {
    invoice: InvoiceData;
    invoiceMatter: InvoiceMatterDetailsDto;
    classifiers: Record<string, ClassifierData>;
    headingId: string;
    showsCapError?: boolean;
}) {
    return (
        <div className="flex min-w-0 flex-[1_1_12rem] flex-col gap-1">
            <div className="flex flex-wrap items-center gap-2">
                <span className="text-foreground-tertiary text-meta font-mono">{invoiceMatter.matterNumber}</span>
                <CaseMarks invoice={invoice} invoiceMatter={invoiceMatter} classifiers={classifiers} />
                {showsCapError ? (
                    <Badge className="bg-danger-tint text-danger-ink">
                        <CircleAlert aria-hidden data-icon="inline-start" />
                        <FormattedMessage id="invoices.case-card.cap-exceeded" />
                    </Badge>
                ) : null}
            </div>
            <h2 id={headingId} className="text-section m-0">
                {invoiceMatter.invoiceMatterName || invoiceMatter.matterName}
            </h2>
        </div>
    );
}

/** The case's people and client, as record chips. */
export function CaseMeta({ invoiceMatter }: { invoiceMatter: InvoiceMatterDetailsDto }) {
    const item = (labelId: string, value: ReactNode) => (
        <span className="flex min-w-0 items-center gap-1.5">
            <span className="shrink-0 whitespace-nowrap">
                <FormattedMessage id={labelId} />
            </span>
            {value}
        </span>
    );
    return (
        <div className="text-meta text-foreground-tertiary flex w-full flex-wrap items-center gap-x-5 gap-y-1.5">
            {invoiceMatter.responsibleEmployee
                ? item(
                      'invoices.responsible-lawyer',
                      <EmployeeNameLink
                          entity={invoiceMatter.responsibleEmployee}
                          to={`/employee/${invoiceMatter.responsibleEmployee.id}`}
                      />
                  )
                : null}
            {invoiceMatter.supervisedByEmployee
                ? item(
                      'invoices.supervised-by',
                      <EmployeeNameLink
                          entity={invoiceMatter.supervisedByEmployee}
                          to={`/employee/${invoiceMatter.supervisedByEmployee.id}`}
                      />
                  )
                : null}
            {item('invoices.client', <PartyName party={invoiceMatter.clientParty} isLink showPartyNumber />)}
        </div>
    );
}

/**
 * The case's names on this invoice besides the one its heading shows: the specification's, and the local-language
 * pair where the firm keeps one. Each only once set.
 */
export function CaseOtherNames({ invoiceMatter }: { invoiceMatter: InvoiceMatterDetailsDto }) {
    const isLocalizedFieldsEnabled = useIsLocalizedFieldsEnabled();
    const names = [
        isLocalizedFieldsEnabled
            ? ['invoices.details.case-name-on-invoice-local-lang', invoiceMatter.invoiceMatterNameLocalLanguage]
            : null,
        ['invoices.details.case-name-on-spec', invoiceMatter.specificationInvoiceMatterName],
        isLocalizedFieldsEnabled
            ? [
                  'invoices.details.case-name-on-spec-local-lang',
                  invoiceMatter.specificationInvoiceMatterNameLocalLanguage,
              ]
            : null,
    ].filter((name): name is [string, string] => !!name?.[1]);
    if (!names.length) {
        return null;
    }
    return (
        <dl className="text-meta text-foreground-tertiary m-0 flex w-full flex-wrap items-center gap-x-5 gap-y-1.5">
            {names.map(([labelId, name]) => (
                <div key={labelId} className="flex min-w-0 items-baseline gap-1.5">
                    <dt className="shrink-0">
                        <FormattedMessage id={labelId} />
                    </dt>
                    <dd className="text-foreground-secondary m-0 min-w-0 font-medium">{name}</dd>
                </div>
            ))}
        </dl>
    );
}

/** The case's four names on this invoice: the names' edit, in the case card's edit or on its own. */
function CaseNameFields({
    control,
    className,
}: {
    control: Control<InvoiceMatterNameDetailsForm>;
    className?: string;
}) {
    const isLocalizedFieldsEnabled = useIsLocalizedFieldsEnabled();
    const headingId = useId();
    const field = (name: keyof Omit<InvoiceMatterNameDetailsForm, 'matterId'>, label: string) => (
        <TextInput control={control} name={name} label={label} maxLength={500} />
    );
    return (
        <div role="group" aria-labelledby={headingId} className={cn('flex flex-col gap-3', className)}>
            <div className="flex flex-col gap-0.5">
                <h3 id={headingId} className="text-meta text-foreground-secondary m-0 font-semibold">
                    <FormattedMessage id="invoices.details.case-name" />
                </h3>
                <p className="text-caption text-foreground-tertiary m-0">
                    <FormattedMessage id="invoices.details.case-name-amendments" />
                </p>
            </div>
            <div className="grid grid-cols-1 gap-x-6 gap-y-3 @xl:grid-cols-2">
                {field('invoiceMatterName', 'invoices.details.case-name-on-invoice')}
                {isLocalizedFieldsEnabled
                    ? field('invoiceMatterNameLocalLanguage', 'invoices.details.case-name-on-invoice-local-lang')
                    : null}
                {field('specificationInvoiceMatterName', 'invoices.details.case-name-on-spec')}
                {isLocalizedFieldsEnabled
                    ? field(
                          'specificationInvoiceMatterNameLocalLanguage',
                          'invoices.details.case-name-on-spec-local-lang'
                      )
                    : null}
            </div>
        </div>
    );
}

/**
 * The names alone in edit, for a case with no case card (a prepayment invoice's): its Case totals card's Edit opens
 * them in place.
 */
export function CaseNamesEdit({
    invoice,
    invoiceMatter,
    canEdit,
    children,
}: {
    invoice: InvoiceData;
    invoiceMatter: InvoiceMatterDetailsDto;
    canEdit: boolean;
    children: (edit: ReactNode) => ReactNode;
}) {
    return (
        <SectionEdit
            id={caseSectionId(invoiceMatter.matterId)}
            titleId="invoices.details.case-name"
            canEdit={canEdit}
            editor={(close) => <CaseNamesEditor invoice={invoice} invoiceMatter={invoiceMatter} close={close} />}
        >
            {children}
        </SectionEdit>
    );
}

function CaseNamesEditor({
    invoice,
    invoiceMatter,
    close,
}: {
    invoice: InvoiceData;
    invoiceMatter: InvoiceMatterDetailsDto;
    close: () => void;
}) {
    const methods = useForm<InvoiceMatterNameDetailsForm>({ defaultValues: caseNameValues(invoiceMatter) });
    const [updateInvoiceMatter] = useUpdateInvoiceMatterMutation();
    return (
        <SectionEditForm
            methods={methods}
            onSave={async (values) => {
                await updateInvoiceMatter({ invoiceId: invoice.id, ...values }).unwrap();
                close();
            }}
            onCancel={close}
        >
            <CaseNameFields control={methods.control} />
        </SectionEditForm>
    );
}

/** The case card's ⋯: Remove from invoice, confirmed by name. Nothing while the case cannot leave the invoice. */
function CaseMoreMenu({ invoice, invoiceMatter }: { invoice: InvoiceData; invoiceMatter: InvoiceMatterDetailsDto }) {
    const isRemovable = useIsMatterRemovable(invoice);
    const [isConfirming, setIsConfirming] = useState(false);
    const [deleteInvoiceMatter, { isLoading }] = useDeleteInvoiceMatterMutation();
    if (!isRemovable) {
        return null;
    }
    const remove = () => {
        void deleteInvoiceMatter({ invoiceId: invoice.id, matterId: invoiceMatter.matterId })
            .unwrap()
            .then(() => setIsConfirming(false))
            .catch(console.error);
    };
    return (
        <>
            <DropdownMenu>
                <DropdownMenuTrigger
                    className={cn(buttonVariants({ variant: 'ghost', size: 'icon-sm' }), 'size-8')}
                    aria-label={t('invoices.case-card.more', { case: invoiceMatter.matterNumber })}
                >
                    <EllipsisIcon className="size-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-auto">
                    <DropdownMenuItem variant="destructive" onClick={() => setIsConfirming(true)}>
                        <FormattedMessage id="invoices.case-card.remove" />
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
            <AlertDialog open={isConfirming} onOpenChange={(open) => !open && setIsConfirming(false)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            <FormattedMessage
                                id="invoices.case-card.remove-confirm-title"
                                values={{ case: invoiceMatter.matterNumber, invoice: invoice.invoiceNumber || 'none' }}
                            />
                        </AlertDialogTitle>
                    </AlertDialogHeader>
                    <AlertDialogDescription className="text-body1 text-foreground">
                        <FormattedMessage id="invoices.case-card.remove-confirm-text" />
                    </AlertDialogDescription>
                    <AlertDialogFooter>
                        <AlertDialogCancel>
                            <FormattedMessage id="cancel" />
                        </AlertDialogCancel>
                        <AlertDialogAction variant="destructive" disabled={isLoading} onClick={remove}>
                            <FormattedMessage id="invoices.case-card.remove-confirm" />
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}

interface Props {
    invoice: InvoiceData;
    invoiceMatter: InvoiceMatterDetailsDto;
    classifiers: Record<string, ClassifierData>;
    isInViewMode: boolean;
    handleStatusLoadingState: (isLoading: boolean) => void;
}

/**
 * A case on the Cases tab of the new design: its header with the case's own approval on a joint invoice, its
 * figures, and an Edit that opens its names, cap, close on send, adjustments and discount in place. While it edits,
 * the inputs that save one by one in the classic card wait for its one Save.
 */
export function InvoiceCaseCard({
    invoice,
    invoiceMatter,
    classifiers,
    isInViewMode,
    handleStatusLoadingState,
}: Props) {
    const headingId = useId();
    const { isCredit } = useInvoice(invoice);
    const isSplit = invoice.isSplit ?? false;
    const { displayDecimalScale } = useTimecardPrecision();
    const { isPaymentCurrencyMode, displayCurrency } = usePaymentCurrencyMode(invoice);
    const isEditing = useEditingSectionId() === caseSectionId(invoiceMatter.matterId);
    const {
        form,
        summaryData,
        initialData,
        isMatterSummaryFetching,
        handleStatusChange,
        handleUpdateFeeArrangementType,
    } = useInvoiceMatterForm({
        invoice,
        invoiceMatter,
        classifiers,
        handleStatusLoadingState,
        isEditing,
    });
    const approval = useCaseApproval({ invoice, invoiceMatter, summaryData, isInViewMode, handleStatusChange });
    const showsCapError = invoiceMatter.hasMatterCapError && !isSplit;
    const lockLabelId = isCredit
        ? 'invoices.case-card.read-only-credit'
        : isSplit
          ? 'invoices.case-card.read-only-split'
          : null;

    // On a narrow card the actions drop under the case rather than squeeze its name.
    const header = (edit: ReactNode) => (
        <div className="flex flex-wrap items-start gap-2">
            <CaseIdentity
                invoice={invoice}
                invoiceMatter={invoiceMatter}
                classifiers={classifiers}
                headingId={headingId}
                showsCapError={showsCapError}
            />
            <CaseApproval approval={approval} handleStatusChange={handleStatusChange} />
            {lockLabelId ? (
                <Badge className={BADGE_CLASS}>
                    <LockIcon aria-hidden data-icon="inline-start" />
                    <FormattedMessage id={lockLabelId} />
                </Badge>
            ) : null}
            {edit}
            {approval.isInvoiceMatterReadOnly ? null : <CaseMoreMenu invoice={invoice} invoiceMatter={invoiceMatter} />}
        </div>
    );

    const body = (
        <>
            {isPaymentCurrencyMode ? <PaymentCurrencyNotice currency={displayCurrency} /> : null}
            {approval.showsFeeArrangementChange ? (
                <DetailsAlert
                    tone="warning"
                    action={
                        <AlertActionButton
                            onClick={() => void handleUpdateFeeArrangementType()}
                            disabled={approval.buttonsDisabled}
                        >
                            <FormattedMessage id="invoices.actions.update-invoice" />
                        </AlertActionButton>
                    }
                >
                    <FormattedMessage id="invoices.alerts.case-fee-arrangement-has-changed" />
                </DetailsAlert>
            ) : null}
            {summaryData ? (
                <CaseFigures
                    invoice={invoice}
                    invoiceMatter={invoiceMatter}
                    form={form}
                    summaryData={summaryData}
                    initialData={initialData}
                    displayCurrency={displayCurrency}
                    isPaymentCurrencyMode={isPaymentCurrencyMode}
                    displayDecimalScale={displayDecimalScale}
                />
            ) : null}
        </>
    );

    return (
        <SectionEdit
            id={caseSectionId(invoiceMatter.matterId)}
            titleId="invoices.details.case-name"
            canEdit={!approval.isInvoiceMatterReadOnly && !!summaryData}
            // The editor draws the case's own header, with the Editing chip, in place of the section's title row.
            className="[&>div:first-child]:hidden"
            editor={(close) =>
                summaryData ? (
                    <CaseEditor
                        invoice={invoice}
                        invoiceMatter={invoiceMatter}
                        form={form}
                        summaryData={summaryData}
                        initialData={initialData}
                        displayCurrency={displayCurrency}
                        isPaymentCurrencyMode={isPaymentCurrencyMode}
                        close={close}
                        header={
                            <div className="flex items-start gap-2">
                                <CaseIdentity
                                    invoice={invoice}
                                    invoiceMatter={invoiceMatter}
                                    classifiers={classifiers}
                                    headingId={`${headingId}-edit`}
                                />
                                <StatusChip
                                    type="neutral"
                                    className="font-semibold"
                                    label={<FormattedMessage id="sections.editing" />}
                                />
                            </div>
                        }
                    />
                ) : null
            }
        >
            {(edit) => (
                <section aria-labelledby={headingId} className={CASE_CARD_CLASS}>
                    {header(edit)}
                    <CaseMeta invoiceMatter={invoiceMatter} />
                    <CaseOtherNames invoiceMatter={invoiceMatter} />
                    <CaseSignOffs invoice={invoice} invoiceMatter={invoiceMatter} />
                    {body}
                    <RecalculatingOverlay
                        invoiceId={invoice.id}
                        isAlsoRecalculating={isMatterSummaryFetching}
                        hideLabel
                    />
                </section>
            )}
        </SectionEdit>
    );
}

/** A joint invoice's per-case step: Review or Approve and Reject while it waits, its mark once done. */
function CaseApproval({
    approval,
    handleStatusChange,
}: {
    approval: ReturnType<typeof useCaseApproval>;
    handleStatusChange: ReturnType<typeof useInvoiceMatterForm>['handleStatusChange'];
}) {
    const {
        isMultiApprovalWorkflowActive,
        isInvoiceDraft,
        isInvoiceReviewed,
        isInvoiceMatterReviewed,
        isInvoiceMatterApproved,
        isMatterStatusUpdatable,
        isRejected,
        hasMatterCapError,
        buttonsDisabled,
        postponeReason,
        openRejectModal,
    } = approval;
    const reasonId = useId();
    if (!isMultiApprovalWorkflowActive) {
        return null;
    }
    const isDone = (isInvoiceMatterReviewed && isInvoiceDraft) || (isInvoiceMatterApproved && isInvoiceReviewed);
    // The reason is a line of its own, which a disabled button can still point at from the Tab order.
    const reasonProps = postponeReason ? { focusableWhenDisabled: true, 'aria-describedby': reasonId } : {};
    return (
        <span className="flex shrink-0 flex-col items-end gap-1">
            <span className="flex items-center gap-2">
                {isRejected ? (
                    <Badge className="bg-danger-tint text-danger-ink">
                        <FormattedMessage id="invoices.actions.rejected" />
                    </Badge>
                ) : null}
                {isInvoiceReviewed && !isInvoiceMatterApproved ? (
                    <Button
                        variant="outline"
                        size="sm"
                        className="h-8"
                        disabled={!isMatterStatusUpdatable || buttonsDisabled}
                        {...reasonProps}
                        onClick={openRejectModal}
                    >
                        <FormattedMessage id="invoices.actions.reject" />
                    </Button>
                ) : null}
                {isDone ? (
                    <Badge className="bg-success-tint text-success-ink">
                        <CheckIcon aria-hidden data-icon="inline-start" />
                        <FormattedMessage
                            id={`invoices.actions.${isInvoiceMatterApproved ? 'approved' : 'reviewed'}`}
                        />
                    </Badge>
                ) : (
                    <Button
                        variant="secondary"
                        size="sm"
                        className="h-8"
                        disabled={!isMatterStatusUpdatable || buttonsDisabled || hasMatterCapError}
                        {...reasonProps}
                        onClick={() =>
                            void handleStatusChange({
                                to: isInvoiceDraft ? InvoiceStatusType.REVIEWED : InvoiceStatusType.APPROVED,
                                transactionActionType: InvoiceTransactionActionType.APPROVED,
                            })
                        }
                    >
                        <FormattedMessage id={`invoices.actions.${isInvoiceDraft ? 'to-REVIEWED' : 'to-APPROVED'}`} />
                    </Button>
                )}
            </span>
            {postponeReason ? (
                <span id={reasonId} className="text-caption text-warning-ink">
                    {postponeReason}
                </span>
            ) : null}
        </span>
    );
}

interface FiguresProps {
    invoice: InvoiceData;
    invoiceMatter: InvoiceMatterDetailsDto;
    form: UseFormReturn<InvoiceMatterForm>;
    summaryData: InvoiceMatterSummary;
    initialData?: InvoiceMatterForm;
    displayCurrency: string;
    isPaymentCurrencyMode: boolean;
    displayDecimalScale: 1 | 2;
}

/** The case's read view: its cap budget when the fee is capped, then its figures as the classic card showed them. */
function CaseFigures({
    invoice,
    invoiceMatter,
    form: { control, setValue },
    summaryData,
    initialData,
    displayCurrency,
    isPaymentCurrencyMode,
    displayDecimalScale,
}: FiguresProps) {
    const { log, discountRate, discountAmount, areCostsDiscounted, totalWithoutVat } = useCaseDiscount({
        control,
        setValue,
        summaryData,
        isPaymentCurrencyMode,
    });
    const { calculationLog } = summaryData;
    const usedHours = getUsedHours(summaryData);
    // A case with no discount holds an empty string, which MoneyDisplay cannot read.
    const money = (amount?: number | string | null) => <MoneyDisplay amount={amount || 0} currency={displayCurrency} />;
    const capLimit = initialData?.adjustmentAmount;
    const hasCapLimit = capLimit != null && `${capLimit}` !== '';
    const closeOnSend = invoiceMatter.closeOnSend ? (
        <CloseOnSendTick
            invoiceId={invoice.id}
            matterId={invoiceMatter.matterId}
            isConfidential={!!invoiceMatter.isConfidential}
            closeOnSend={invoiceMatter.closeOnSend}
            isReadCell
        />
    ) : null;
    // Ticked, with its record lines, the choice takes a row of its own under the figures; the firm's rule alone keeps
    // it in its cell.
    const closeOnSendIsLong = !!invoiceMatter.closeOnSend?.isRequested;

    return (
        <>
            {summaryData.feeArrangementTypeCode === 'HOURLY_CAPPED' ? (
                <CapFeeBudgetBox
                    variant="tile"
                    budget={summaryData.matterBillingSummary?.budget?.capTotal ?? 0}
                    remaining={summaryData.matterBillingSummary?.budget?.remainingCap ?? 0}
                    used={summaryData.matterBillingSummary?.budget?.usedCap ?? 0}
                    remainingInvoice={summaryData.matterBillingSummary?.budget?.remainingInvoicedOnly ?? 0}
                    currency={displayCurrency}
                    isInvoice
                />
            ) : null}
            <dl className={CASE_FIGURES_CLASS}>
                <Figure labelId="invoices.details.used-hours">{formatToHours(usedHours, displayDecimalScale)}</Figure>
                <Figure labelId="invoices.details.billed-hours">
                    {formatToHours(calculationLog?.fees?.billedTimeInHours ?? 0, displayDecimalScale)}
                </Figure>
                <Figure labelId="invoices.case-card.cap-limit">
                    {hasCapLimit ? (
                        <FormattedMessage
                            id={
                                initialData?.areCostsCapped
                                    ? 'invoices.case-card.cap-with-expenses'
                                    : 'invoices.case-card.cap-without-expenses'
                            }
                            values={{ amount: money(capLimit) }}
                        />
                    ) : (
                        <FormattedMessage id="invoices.case-card.no-cap" />
                    )}
                </Figure>
                {closeOnSendIsLong ? null : closeOnSend}
                <Figure labelId="invoices.details.fees-total">{money(log?.fees?.totalAfterMatterAdjustments)}</Figure>
                <Figure labelId="invoices.details.exp-total-with-vat">
                    {money(log?.expensesWithVat?.totalAfterMatterAdjustments)}
                </Figure>
                <Figure labelId="invoices.details.exp-total-wo-vat">
                    {money(log?.expensesWithoutVat?.totalAfterMatterAdjustments)}
                </Figure>
                <Figure labelId="invoices.details.discount">
                    {`${discountRate || 0}% · `}
                    {money(isPaymentCurrencyMode ? log?.total?.matterDiscountAmount : discountAmount)}
                    {areCostsDiscounted ? ` · ${t('invoices.details.with-expenses')}` : null}
                </Figure>
                <Figure labelId="invoices.details.net-total" strong>
                    {money(totalWithoutVat)}
                </Figure>
                {closeOnSendIsLong ? closeOnSend : null}
            </dl>
        </>
    );
}

const ADJUSTMENTS = [
    'adjustmentAmountForFees',
    'adjustmentAmountForExpensesWithVat',
    'adjustmentAmountForExpensesWithoutVat',
] as const;

/**
 * The adjustments to the cent. A payment-currency case's arrive converted from the invoice currency with every
 * decimal, which their fields would print as they are.
 */
const toCents = (form: InvoiceMatterForm): InvoiceMatterForm => ({
    ...form,
    ...Object.fromEntries(
        ADJUSTMENTS.map((name) => [name, form[name] == null ? form[name] : Math.round(Number(form[name]) * 100) / 100])
    ),
});

/** What Save sends: an adjustment left at the cent it was shown at goes back exactly as it came, so nothing drifts. */
const withExactAdjustments = (values: InvoiceMatterForm, initialData: InvoiceMatterForm): InvoiceMatterForm => {
    const shown = toCents(initialData);
    return {
        ...values,
        ...Object.fromEntries(
            ADJUSTMENTS.filter((name) => values[name] === shown[name]).map((name) => [name, initialData[name]])
        ),
    };
};

interface EditorProps {
    invoice: InvoiceData;
    invoiceMatter: InvoiceMatterDetailsDto;
    form: UseFormReturn<InvoiceMatterForm>;
    summaryData: InvoiceMatterSummary;
    initialData?: InvoiceMatterForm;
    displayCurrency: string;
    isPaymentCurrencyMode: boolean;
    header: ReactNode;
    close: () => void;
}

/**
 * The case card in edit. Its fields are the case form the classic card saves field by field; here they only change
 * the form, and Save sends the names, the close-on-send choice and the adjustments that differ from the saved ones,
 * in that order. Cancel puts the form back to the saved values.
 */
function CaseEditor({
    invoice,
    invoiceMatter,
    form,
    summaryData,
    initialData,
    displayCurrency,
    isPaymentCurrencyMode,
    header,
    close,
}: EditorProps) {
    const { control, setValue, getValues, reset } = form;
    const namesForm = useForm<InvoiceMatterNameDetailsForm>({ defaultValues: caseNameValues(invoiceMatter) });
    const [pendingCloseOnSend, setPendingCloseOnSend] = useState<UpdateCloseOnSendDto | null>(null);
    const [updateInvoiceMatter] = useUpdateInvoiceMatterMutation();
    const [updateCloseOnSend] = useUpdateInvoiceMatterCloseOnSendMutation();
    const [updateAdjustments] = useUpdateInvoiceMatterAdjustmentsMutation();
    const isVatExemptExcludedFromDiscount = useIsFeatureEnabled(FeatureFlag.EXCLUDE_VAT_EXEMPT_FROM_DISCOUNT);
    const isCaseAssignmentEnabled = useIsFeatureEnabled(FeatureFlag.CASE_ASSIGNMENT_ENABLED);
    const { handleOnCapLimitChange, applyIncludeExpenses, feesForCappedAssignments } = useCapLimitAdjustments({
        control,
        setValue,
        summaryData,
        isVatExemptExcludedFromDiscount,
        isPaymentCurrencyMode,
    });
    const { log, totalBeforeDiscount, handleOnDiscountAmountChange, handleOnDiscountPercentageChange } =
        useCaseDiscount({ control, setValue, summaryData, isPaymentCurrencyMode });
    const amountClass = cn('w-44', isPaymentCurrencyMode && paymentCurrencyInputClassName);
    const money = (amount?: number) => <MoneyDisplay amount={amount ?? 0} currency={displayCurrency} />;

    // Opening, the fields take the adjustments to the cent, as the saved state Cancel returns to.
    useEffect(() => {
        if (initialData) {
            reset(toCents(initialData));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- once, as the editor opens
    }, []);

    const save = async (values: InvoiceMatterForm) => {
        if (namesForm.formState.isDirty) {
            await updateInvoiceMatter({ invoiceId: invoice.id, ...namesForm.getValues() }).unwrap();
        }
        if (pendingCloseOnSend) {
            await updateCloseOnSend({
                invoiceId: invoice.id,
                matterId: invoiceMatter.matterId,
                ...pendingCloseOnSend,
            }).unwrap();
        }
        const adjustments = mapInvoiceMatterFormDataToAdjustmentDto(
            initialData ? withExactAdjustments(values, initialData) : values,
            invoice.id
        );
        if (!initialData || !isEqual(adjustments, mapInvoiceMatterFormDataToAdjustmentDto(initialData, invoice.id))) {
            await updateAdjustments(adjustments).unwrap();
        }
        close();
    };

    const cancel = () => {
        reset();
        close();
    };

    const adjustment = (
        name:
            | 'adjustmentAmountForFees'
            | 'adjustmentAmountForExpensesWithVat'
            | 'adjustmentAmountForExpensesWithoutVat',
        labelId: string,
        before: ReactNode,
        disabledReasonId?: string
    ) => (
        <div className="flex min-w-0 flex-col gap-1">
            <div className="flex flex-wrap items-end gap-x-3 gap-y-1">
                <DecimalInput
                    allowNegative
                    name={name}
                    control={control}
                    label={labelId}
                    disabled={!!disabledReasonId}
                    placeholder="0.00"
                    shouldTranslatePlaceholder={false}
                    suffixComponent={displayCurrency}
                    // The label runs past the narrow amount, on one line.
                    wrapperClassName={cn(amountClass, '[&_label]:whitespace-nowrap')}
                />
                <span className="text-meta text-foreground-tertiary flex flex-col pb-2 tabular-nums">{before}</span>
            </div>
            {disabledReasonId ? (
                <span className="text-caption text-foreground-tertiary">
                    <FormattedMessage id={disabledReasonId} />
                </span>
            ) : null}
        </div>
    );

    return (
        <>
            {header}
            <SectionEditForm methods={form} onSave={save} onCancel={cancel}>
                <div className="flex flex-col gap-4">
                    {isPaymentCurrencyMode ? <PaymentCurrencyNotice currency={displayCurrency} /> : null}
                    <CaseNameFields control={namesForm.control} className="border-divider border-b pb-4" />
                    <div className="grid grid-cols-1 gap-x-6 gap-y-4 @xl:grid-cols-2">
                        <div className="flex flex-wrap items-end gap-3">
                            <DecimalInput
                                name="adjustmentAmount"
                                control={control}
                                label="invoices.details.cap-limit"
                                tooltip={<InfoTooltip text="invoices.details.case-cap-adjustment-tooltip" />}
                                placeholder="invoices.case-card.no-cap"
                                suffixComponent={displayCurrency}
                                wrapperClassName={amountClass}
                                onChange={(event) => handleOnCapLimitChange(event.target.value)}
                            />
                            <Button
                                variant="outline"
                                size="icon"
                                className="-ms-1.5 mb-px size-9"
                                aria-label={t('invoices.case-card.recalculate-cap')}
                                onClick={() => handleOnCapLimitChange(getValues('adjustmentAmount'))}
                            >
                                <RefreshCwIcon className="size-4" />
                            </Button>
                            <div className="pb-2">
                                <CheckboxInput
                                    label="invoices.details.include-expenses"
                                    control={control}
                                    name="areCostsCapped"
                                    keepCheckbox
                                    onChange={applyIncludeExpenses}
                                />
                            </div>
                        </div>
                        {invoiceMatter.closeOnSend ? (
                            <div className="self-end">
                                <CloseOnSendTick
                                    invoiceId={invoice.id}
                                    matterId={invoiceMatter.matterId}
                                    isConfidential={!!invoiceMatter.isConfidential}
                                    closeOnSend={invoiceMatter.closeOnSend}
                                    defer={{ pending: pendingCloseOnSend, onChange: setPendingCloseOnSend }}
                                />
                            </div>
                        ) : null}
                        {adjustment(
                            'adjustmentAmountForFees',
                            'invoices.case-card.fees-adjustment',
                            <>
                                <span>
                                    {t('invoices.details.fees-before-adjustment')}{' '}
                                    {money(log?.fees?.totalBeforeMatterAdjustments)}
                                </span>
                                {isCaseAssignmentEnabled ? (
                                    <span>
                                        {t('invoices.details.fees-for-capped-assignments')}{' '}
                                        {money(feesForCappedAssignments)}
                                    </span>
                                ) : null}
                            </>
                        )}
                        {adjustment(
                            'adjustmentAmountForExpensesWithVat',
                            'invoices.case-card.vat-applied-adjustment',
                            <span>
                                <FormattedMessage
                                    id="invoices.case-card.before-adjustment"
                                    values={{ amount: money(log?.expensesWithVat?.totalBeforeMatterAdjustments) }}
                                />
                            </span>
                        )}
                        {adjustment(
                            'adjustmentAmountForExpensesWithoutVat',
                            'invoices.case-card.vat-exempt-adjustment',
                            <span>
                                <FormattedMessage
                                    id="invoices.case-card.before-adjustment"
                                    values={{ amount: money(log?.expensesWithoutVat?.totalBeforeMatterAdjustments) }}
                                />
                            </span>,
                            isVatExemptExcludedFromDiscount
                                ? 'invoices.details.vat-exempt-expenses-adjustment-tooltip'
                                : undefined
                        )}
                        <fieldset className="m-0 flex min-w-0 flex-col gap-1.5 border-0 p-0 @xl:col-span-2">
                            <legend className={cn(FIELD_LABEL_CLASS, 'mb-1.5 p-0')}>
                                <FormattedMessage
                                    id="invoices.case-card.discount-legend"
                                    values={{ amount: money(totalBeforeDiscount) }}
                                />
                            </legend>
                            <div className="flex flex-wrap items-start gap-3">
                                <PercentageInput
                                    allowNegative
                                    name="discountRate"
                                    decimalPlaces={7}
                                    control={control}
                                    validate={validateDiscountRate}
                                    suffixComponent="%"
                                    wrapperClassName={cn(
                                        'w-28',
                                        isPaymentCurrencyMode && paymentCurrencyInputClassName
                                    )}
                                    inputProps={{ 'aria-label': t('invoices.totals-tab.bulk-discount.rate-label') }}
                                    onChange={(event) => handleOnDiscountPercentageChange(event.target.value)}
                                />
                                <DecimalInput
                                    onlyNegative
                                    name="discountAmount"
                                    control={control}
                                    suffixComponent={displayCurrency}
                                    wrapperClassName={amountClass}
                                    inputProps={{
                                        'aria-label': t('invoices.details.discount-currency', {
                                            currency: displayCurrency,
                                        }),
                                    }}
                                    onChange={(event) => handleOnDiscountAmountChange(event.target.value)}
                                />
                                <div className="pt-2">
                                    <CheckboxInput
                                        label="invoices.details.include-expenses"
                                        control={control}
                                        name="areCostsDiscounted"
                                        keepCheckbox
                                    />
                                </div>
                            </div>
                        </fieldset>
                    </div>
                </div>
            </SectionEditForm>
            {/* The names and the close-on-send choice live outside the case form, so its own guard misses them;
                registered after it, this one decides for all three. */}
            <SectionUnsavedChangesGuard
                when={form.formState.isDirty || namesForm.formState.isDirty || !!pendingCloseOnSend}
            />
        </>
    );
}
